import React, { useState, useEffect } from "react";
import "./PrintingSection.css";
import { useTranslation } from "react-i18next";
import { getCurrencyForLocale, formatPrice } from "../../config/locales";
import { useLocation } from "react-router-dom";
import useLangNavigate from "./../common/useLangNavigate";
import bookImage from "../../assets/img/others/print_book.jpeg";
import { toast } from "react-toastify";
import FeatureCard from "../common/feature-card/FeatureCard";
import { postUploadDocx, postSubmitDocxForPrinting } from "../../core/api/Api";
import PrintingDisclaimerModal from "./PrintingDisclaimerModal";

import dosCarreColle from "../../assets/png/reliure-dos-carre.png";
import remborde from "../../assets/png/reliure-remborde.png";
import agrafe from "../../assets/png/reliure-agrafe.png";
import spirale from "../../assets/png/reliure-spirale.png";
import { showToast } from "../../core/common-service/CommonService";

const PrintingSection = () => {
  const location = useLocation();
  const [file, setFile] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [price, setPrice] = useState(0);
  const [copies, setCopies] = useState(5);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);

  // New states for printing options - with default values
  const [bindingType, setBindingType] = useState("Dos Carré Collé");
  const [format, setFormat] = useState("A5 Portrait (14,8x21 cm)");
  const [innerPaper, setInnerPaper] = useState("Standard (90g blanc)");
  const [coverFinish, setCoverFinish] = useState("Mat (Effet matifié)");
  const [innerPrinting, setInnerPrinting] = useState("Noir et blanc");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [address, setAddress] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useLangNavigate();
  const { t, i18n } = useTranslation();
  const currency = getCurrencyForLocale(i18n.language);

  // Printing options data
  const bindingOptions = [
    {
      value: "Dos Carré Collé",
      name: t("printing.binding_perfect_bound"),
      description: t("printing.binding_perfect_bound_subtitle"),
      img: dosCarreColle,
    },
    {
      value: "Rembordé",
      name: t("printing.binding_sewn"),
      description: t("printing.binding_sewn_subtitle"),
      img: remborde,
    },
    {
      value: "Agrafé",
      name: t("printing.binding_stapled"),
      description: t("printing.binding_stapled_subtitle"),
      img: agrafe,
    },
    {
      value: "Spirale",
      name: t("printing.binding_spiral"),
      description: t("printing.binding_spiral_subtitle"),
      img: spirale,
    },
  ];

  const formatOptions = [
    {
      value: "Pocket (11x17 cm)",
      name: t("printing.format_pocket"),
      description: "11x17 cm",
    },
    {
      value: "Grand livre (16x24 cm)",
      name: t("printing.format_grand_livre"),
      description: "16x24 cm",
    },
    {
      value: "A4 Portrait (21x29,7 cm)",
      name: t("printing.format_a4_portrait"),
      description: "21x29,7 cm",
    },
    {
      value: "A5 Portrait (14,8x21 cm)",
      name: t("printing.format_a5_portrait"),
      description: "14,8x21 cm",
    },
    {
      value: "Carré (21x21 cm)",
      name: t("printing.format_carre"),
      description: "21x21 cm",
    },
    {
      value: "A5 Paysage (21x14,8 cm)",
      name: t("printing.format_a5_paysage"),
      description: "21x14,8 cm",
    },
  ];

  const innerPaperOptions = [
    {
      value: "Standard (90g blanc)",
      name: t("printing.paper_standard"),
      description: "90g blanc",
    },
    {
      value: "Doux (80g blanc)",
      name: t("printing.paper_soft"),
      description: "80g blanc",
    },
    {
      value: "Crème / Ancien (80g beige)",
      name: t("printing.paper_cream"),
      description: "80g beige",
    },
    {
      value: "Lisse satin / photo (115g blanc)",
      name: t("printing.paper_luxury"),
      description: "115g blanc",
    },
  ];

  const coverFinishOptions = [
    {
      value: "Brillant (Effet Glossy)",
      name: t("printing.cover_glossy"),
      description: t("printing.cover_glossy_subtitle"),
    },
    {
      value: "Mat (Effet matifié)",
      name: t("printing.cover_matte"),
      description: t("printing.cover_matte_subtitle"),
    },
    {
      value: "Soft Touch (Toucher velours / peau de pêche)",
      name: t("printing.cover_soft_touch"),
      description: t("printing.cover_soft_touch_subtitle"),
    },
  ];

  const innerPrintingOptions = [
    {
      value: "Couleur",
      name: t("printing.printing_color"),
      description: t("printing.printing_color_subtitle"),
    },
    {
      value: "Noir et blanc",
      name: t("printing.printing_bw"),
      description: t("printing.printing_bw_subtitle"),
    },
  ];

  const calculatePrice = (file, copies, innerPrintingValue = null) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("copies", copies);

    // Include inner_printing if it's already selected
    if (innerPrintingValue) {
      formData.append("inner_printing", innerPrintingValue);
    }

    setLoading(true);

    postUploadDocx(formData)
      .then((response) => {
        setPageCount(response.data.pageCount);
        setPrice(response.data.price);
      })
      .catch(() => {
        showToast({ type: "error", message: t("error.unknown_error") });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Handle preloaded file from book generation
  useEffect(() => {
    const stateData = location.state;

    if (stateData && stateData.preloadedFile && stateData.fromBookGeneration) {
      setFile(stateData.preloadedFile);
      // Automatically calculate price for preloaded file
      calculatePrice(stateData.preloadedFile, copies, innerPrinting);
    }
  }, [location.state]);

  // Calculate initial price when file is uploaded
  useEffect(() => {
    if (file) {
      calculatePrice(file, copies, innerPrinting);
    }
  }, [file]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (
      selectedFile &&
      selectedFile.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      setFile(selectedFile);

      const targetElement = document.getElementById("binding_type_section");
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "start",
        });
      }
    }
  };

  const handleCopiesChange = (e) => {
    const newCopies = e.target.value;
    setCopies(newCopies);
    if (file && newCopies >= 5) {
      calculatePrice(file, newCopies, innerPrinting);
    }
  };

  const handleBindingTypeSelect = (value) => {
    setBindingType(value);

    setTimeout(() => {
      const targetElement = document.getElementById("format_section");
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "start",
        });
      }
    }, 100);
  };

  const handleFormatSelect = (value) => {
    setFormat(value);

    setTimeout(() => {
      const targetElement = document.getElementById("inner_paper_section");
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "start",
        });
      }
    }, 100);
  };

  const handleInnerPaperSelect = (value) => {
    setInnerPaper(value);

    setTimeout(() => {
      const targetElement = document.getElementById("cover_finish_section");
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "start",
        });
      }
    }, 100);
  };

  const handleCoverFinishSelect = (value) => {
    setCoverFinish(value);

    setTimeout(() => {
      const targetElement = document.getElementById("inner_printing_section");
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "start",
        });
      }
    }, 100);
  };

  const handleInnerPrintingSelect = (value) => {
    setInnerPrinting(value);

    // Recalculate price when inner printing option changes
    if (file) {
      calculatePrice(file, copies, value);
    }

    setTimeout(() => {
      const targetElement = document.getElementById("copies_section");
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "start",
        });
      }
    }, 100);
  };

  const handleSubmit = () => {
    // Validate minimum order quantity
    if (copies < 5) {
      toast.error(t("printing.minimum_copies_error"));
      return;
    }

    // Validate all printing options are selected
    if (
      !bindingType ||
      !format ||
      !innerPaper ||
      !coverFinish ||
      !innerPrinting
    ) {
      toast.error(t("printing.please_fill_all_fields"));
      return;
    }

    // Validate name and address fields
    if (
      !firstName ||
      !lastName ||
      !address ||
      !postalCode ||
      !city ||
      !phoneNumber
    ) {
      toast.error(t("printing.please_fill_all_fields"));
      return;
    }

    // Show disclaimer modal instead of navigating directly
    setShowDisclaimerModal(true);
  };

  const handleProceedToPayment = () => {
    setShowDisclaimerModal(false);
    setLoading(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("copies", copies);
    formData.append("binding_type", bindingType);
    formData.append("format", format);
    formData.append("inner_paper", innerPaper);
    formData.append("cover_finish", coverFinish);
    formData.append("inner_printing", innerPrinting);
    formData.append("first_name", firstName);
    formData.append("last_name", lastName);
    formData.append("address", address);
    formData.append("postal_code", postalCode);
    formData.append("city", city);
    formData.append("phone_number", phoneNumber);

    postSubmitDocxForPrinting(formData)
      .then((res) => {
        const printingOrderId = res?.data?.printing_order_id;
        navigate("/printing-payment", "", {
          state: {
            printingOrderId,
            price,
            copies,
          },
        });
      })
      .catch(() => {
        showToast({ type: "error", message: t("printing_file_submit_failed") });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <section className="printing-section py-100">
      <div className="container">
        <div className="content-wrapper">
          {/* Right Content */}
          <div className="right-content">
            <h1>{t("book_format_printing")}</h1>
            <img src={bookImage} alt="Book Printing Example" />
            <div style={{ marginTop: "20px" }}>
              <ul>
                {t("printing_specs", { returnObjects: true }).map(
                  (item, index) => (
                    <li key={index}>{item.text}</li>
                  ),
                )}
              </ul>
            </div>
          </div>

          {/* Left Content */}
          <div className="left-content">
            <h1>{t("upload_your_book")}</h1>
            <div className="custom-file-upload">
              <label className="custom-file-label" htmlFor="file-upload">
                {file ? file.name : t("choose_file")}
              </label>
              <input
                id="file-upload"
                type="file"
                accept=".docx"
                onChange={handleFileChange}
                className="file-input-hidden"
              />
            </div>
            <p className="manufacturing-delay-text">
              {t("printing.manufacturing_delay")}
            </p>

            {/* Binding Type */}
            <div id="binding_type_section" className="printing-options-section">
              <h3>{t("printing.binding_type")}</h3>
              <div className="row">
                {bindingOptions.map((option) => (
                  <FeatureCard
                    key={option.value}
                    img={option.img}
                    name={option.name}
                    description={option.description}
                    is_selected={bindingType === option.value}
                    onClick={() => handleBindingTypeSelect(option.value)}
                    showArrow={false}
                    colClasses="col-lg-3 col-md-6 col-sm-6"
                  />
                ))}
              </div>
            </div>

            {/* Format */}
            {bindingType && (
              <div id="format_section" className="printing-options-section">
                <h3>{t("printing.format")}</h3>
                <div className="row">
                  {formatOptions.map((option) => (
                    <FeatureCard
                      key={option.value}
                      name={option.name}
                      description={option.description}
                      is_selected={format === option.value}
                      onClick={() => handleFormatSelect(option.value)}
                      showArrow={false}
                      colClasses="col-lg-3 col-md-6 col-sm-6"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Inner Paper */}
            {format && (
              <div
                id="inner_paper_section"
                className="printing-options-section"
              >
                <h3>{t("printing.inner_paper")}</h3>
                <div className="row">
                  {innerPaperOptions.map((option) => (
                    <FeatureCard
                      key={option.value}
                      name={option.name}
                      description={option.description}
                      is_selected={innerPaper === option.value}
                      onClick={() => handleInnerPaperSelect(option.value)}
                      showArrow={false}
                      colClasses="col-lg-3 col-md-6 col-sm-6"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Cover Finish */}
            {innerPaper && (
              <div
                id="cover_finish_section"
                className="printing-options-section"
              >
                <h3>{t("printing.cover_finish")}</h3>
                <div className="row">
                  {coverFinishOptions.map((option) => (
                    <FeatureCard
                      key={option.value}
                      name={option.name}
                      description={option.description}
                      is_selected={coverFinish === option.value}
                      onClick={() => handleCoverFinishSelect(option.value)}
                      showArrow={false}
                      colClasses="col-lg-4 col-md-6"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Inner Printing */}
            {coverFinish && (
              <div
                id="inner_printing_section"
                className="printing-options-section"
              >
                <h3>{t("printing.inner_printing")}</h3>
                <div className="row">
                  {innerPrintingOptions.map((option) => (
                    <FeatureCard
                      key={option.value}
                      name={option.name}
                      description={option.description}
                      is_selected={innerPrinting === option.value}
                      onClick={() => handleInnerPrintingSelect(option.value)}
                      showArrow={false}
                      colClasses="col-lg-6 col-md-6"
                    />
                  ))}
                </div>
              </div>
            )}
            {file && (
              <>
                {/* Number of Copies */}
                {innerPrinting && (
                  <div id="copies_section" className="copies-input-container">
                    <label htmlFor="copies">{t("number_of_copies")}</label>
                    <input
                      type="number"
                      id="copies"
                      min="5"
                      value={copies}
                      onChange={handleCopiesChange}
                    />
                  </div>
                )}

                {innerPrinting && (
                  <div className="result">
                    <p>
                      {t("page_count")}:{" "}
                      <span className="value">{pageCount}</span>
                    </p>
                    <p>
                      {t("price")}: <span className="value">{formatPrice(price, i18n.language)}</span>
                    </p>
                  </div>
                )}

                {innerPrinting && (
                  <div className="form-fields">
                    <h3>{t("shipping_address")}</h3>
                    {/* First Name */}
                    <div className="input-container">
                      <label htmlFor="first-name" className="input-label">
                        {t("first_name")}
                      </label>
                      <input
                        type="text"
                        id="first-name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="input-field"
                      />
                    </div>

                    {/* Last Name */}
                    <div className="input-container">
                      <label htmlFor="last-name" className="input-label">
                        {t("last_name")}
                      </label>
                      <input
                        type="text"
                        id="last-name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="input-field"
                      />
                    </div>

                    {/* Address, Postal Code, and City Inputs */}
                    <div className="input-container">
                      <label htmlFor="address" className="input-label">
                        {t("address")}
                      </label>
                      <input
                        type="text"
                        id="address"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        className="input-field"
                      />
                    </div>

                    <div className="input-container">
                      <label htmlFor="postal-code" className="input-label">
                        {t("postal_code")}
                      </label>
                      <input
                        id="postal-code"
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        className="input-field"
                      />
                    </div>

                    <div className="input-container">
                      <label htmlFor="city" className="input-label">
                        {t("city")}
                      </label>
                      <input
                        type="text"
                        id="city"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        className="input-field"
                      />
                    </div>

                    {/* Phone Number */}
                    <div className="input-container">
                      <label htmlFor="phone-number" className="input-label">
                        {t("phone_number")}
                      </label>
                      <input
                        type="tel"
                        id="phone-number"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="input-field"
                      />
                    </div>

                    <div className="submit-button-container">
                      <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className={`submit-button ${loading ? "loading" : ""}`}
                      >
                        {loading ? t("processing") : t("submit")}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Disclaimer Modal */}
      <PrintingDisclaimerModal
        isOpen={showDisclaimerModal}
        onClose={() => setShowDisclaimerModal(false)}
        onProceed={handleProceedToPayment}
      />
    </section>
  );
};

export default PrintingSection;
